import { useState, useEffect } from "react";
import axios from "axios";
import { Alert, Container, TextField, Tooltip } from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import { Search } from "@mui/icons-material";

export default function AddOrEditPersonal() {
  const {
    WAREHOUSE_ENDPOINT,
    POSITION_ENDPOINT,
    CONTRACTOR_API,
    PERSONAL_API,
    personalModel,
    setPersonalModel,
    locations,
    setCity,
    cities,
    setCityFilterCode,
    config,
  } = useGlobalContext();

  const [positionData, setPositionData] = useState(null);
  const [contractorsData, setContractorsData] = useState(null);
  const [warehouses, setWarehouses] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState({ show: false });

  const getWarehouses = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${WAREHOUSE_ENDPOINT}/`, requestConfig);

    setWarehouses(data?.data);
  };

  const getPositionData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${POSITION_ENDPOINT}/`, requestConfig);

    setPositionData(data?.data);
  };

  const getContractorsData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CONTRACTOR_API}/`, requestConfig);

    setContractorsData(data?.data);
  };

  useEffect(() => {
    getPositionData();
    getContractorsData();
    getWarehouses();
  }, []);

  const handleChange = (e) => {
    setPersonalModel({
      ...personalModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleIdentificationTypeChange = (identificationType) => {
    if (identificationType) {
      setPersonalModel({
        ...personalModel,
        IdentificationType: identificationType,
      });
    }
  };

  const handleStateChange = (currentState) => {
    if (!currentState) {
      setPersonalModel({
        ...personalModel,
        StateId: 0,
        State: null,
        CityId: 0,
        City: null,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      StateId: currentState.Id,
      State: currentState,
      CityId: 0,
      City: null,
    });
  };

  const handleContractorChange = (currentContractor) => {
    if (!currentContractor) {
      setPersonalModel({
        ...personalModel,
        Contractor: {},
        ContractorId: null,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      Contractor: currentContractor,
      ContractorId: currentContractor.Id,
    });
  };

  const handlePositionChange = (currentPosition) => {
    if (!currentPosition) {
      setPersonalModel({
        ...personalModel,
        Position: {},
        PositionId: null,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      Position: currentPosition,
      PositionId: currentPosition.Id,
    });
  };

  const handleWarehouseChange = (e) => {
    if (!e) {
      setPersonalModel({
        ...personalModel,
        Warehouse: null,
        WarehouseId: 0,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      Warehouse: e,
      WarehouseId: e.Id,
    });
  };

  const handleCityChange = (currentCity) => {
    if (!currentCity) {
      setPersonalModel({
        ...personalModel,
        CityId: 0,
        City: null,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      CityId: currentCity.Id,
      City: currentCity,
    });
    setCityFilterCode(currentCity.Code);
  };

  const [states, setStates] = useState(null);
  const [citiesData, setCities] = useState(null);

  useEffect(() => {
    if (personalModel?.StateId > 0) {
      const selectedState = states?.find(
        (element) => element.Id === personalModel?.StateId
      );
      if (selectedState) {
        setCity(selectedState.Code);
      }
    }
  }, [personalModel?.StateId]);

  useEffect(() => {
    if (locations.Ok && locations.Data.length > 0) {
      setStates(locations.Data);
    }
  }, [locations]);

  useEffect(() => {
    if (cities?.Ok && cities.Data.length > 0) {
      setCities(cities.Data);
    }
  }, [cities]);

  const isSearchAvailable = () =>
    personalModel?.Identification?.replaceAll(" ", "").length > 0;

  const getPersonalInfoByIdentification = async () => {
    try {
      if (personalModel?.Identification?.replaceAll(" ", "").length === 0) {
        return;
      }
      setShowWarning({ show: false });
      setIsLoading(true);
      const filteringConfig = {
        ...config,
        headers: {
          ...config.headers,
          Filter: JSON.stringify([
            {
              Name: "Identification",
              FriendlyName: localize.identification,
              FilterValue: { Value: personalModel.Identification },
              Value: personalModel.Identification,
            },
            {
              Name: "IdentificationType",
              FriendlyName: localize.identification_type,
              FilterValue: { Value: personalModel.IdentificationType.Id },
              Value: personalModel.IdentificationType.Id,
            },
          ]),
          pagination: -1,
        },
      };
      const data = await axios.get(PERSONAL_API, filteringConfig);

      if (data?.data?.Data && data?.data?.Data.length > 0) {
        setPersonalModel({
          ...data?.data?.Data[0],
          IdentificationType: {
            Id: data?.data?.Data[0].IdentificationType,
            Name: data?.data?.Data[0].IdentificationType,
          },
        });

        setShowWarning({
          show: true,
          severity: "warning",
          message: localize.found_personal_warning,
        });
        return;
      }

      setShowWarning({
        show: true,
        severity: "info",
        message: localize.no_data,
      });
    } catch (error) {
      setShowWarning({
        show: true,
        severity: "error",
        message: `${localize.error_unexpected}: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter") {
      getPersonalInfoByIdentification();
    }
  };

  return (
    <div>
      <Container
        sx={{ paddingLeft: "0px !important;", paddingRight: "0px !important;" }}
        style={{ width: "100%" }}
      >
        <div className="w-full">
          <section className="flex justify-center content-center items-center ">
            <section id="ReportData" className="w-full">
              <div className="flex gap-2 my-3 items-center">
                <Combobox
                  isRequired
                  id="IdentificationType"
                  handleValueChange={handleIdentificationTypeChange}
                  fieldValue={personalModel?.IdentificationType ?? null}
                  fieldOptions={[
                    {
                      Id: "Cédula de ciudadanía",
                      Name: "Cédula de ciudadanía",
                    },
                    { Id: "Nit", Name: "Nit" },
                    { Id: "Pasaporte", Name: "Pasaporte" },
                    {
                      Id: "Cédula de extranjería",
                      Name: "Cédula de extranjería",
                    },
                    { Id: "Otro", Name: "Otro" },
                  ]}
                  fieldLabel={localize.identification_type}
                />

                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Identification"
                  value={personalModel.Identification}
                  id="Identification"
                  label={localize.identification}
                  variant="outlined"
                  onChange={handleChange}
                  onKeyUp={onKeyUp}
                  autoComplete="off"
                />

                <Tooltip title={localize.personal_search_tooltip}>
                  <button
                    disabled={!isSearchAvailable()}
                    style={{
                      color: isSearchAvailable() ? "#10366e" : "lightgray",
                    }}
                    onClick={getPersonalInfoByIdentification}
                  >
                    <Search
                      sx={{
                        width: "24px",
                        height: "24px",
                      }}
                    />
                  </button>
                </Tooltip>
              </div>

              {showWarning?.show && (
                <Alert className="mb-3" severity={showWarning.severity}>
                  <p className="report_little_text">{showWarning.message}</p>
                </Alert>
              )}

              <div className="flex justify-between my-3">
                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="SurName"
                  id="SurName"
                  value={personalModel.SurName}
                  label={localize.surName}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
                <div className="mx-2" />
                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="LastName"
                  id="LastName"
                  value={personalModel.LastName}
                  label={localize.lastName}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>

              <div className="flex justify-between my-3">
                <Combobox
                  id="Contractors"
                  handleValueChange={handleContractorChange}
                  fieldValue={personalModel?.Contractor}
                  fieldOptions={contractorsData?.Data ?? []}
                  fieldLabel={localize.contractor}
                />

                <div className="mx-2" />

                <Combobox
                  id="Position"
                  handleValueChange={handlePositionChange}
                  fieldValue={personalModel?.Position}
                  fieldOptions={positionData?.Data ?? []}
                  fieldLabel={localize.position}
                />
              </div>

              {/* States */}
              <div className="flex justify-between my-3">
                <Combobox
                  isRequired
                  id="State"
                  handleValueChange={handleStateChange}
                  fieldValue={personalModel?.State}
                  fieldOptions={states ?? []}
                  fieldLabel={localize.department}
                />

                {/* Cities */}
                <div className="mx-2" />
                <Combobox
                  isRequired
                  id="City"
                  handleValueChange={handleCityChange}
                  fieldValue={personalModel?.City}
                  fieldOptions={personalModel?.State ? citiesData ?? [] : []}
                  fieldLabel={localize.city}
                />
              </div>

              <div className="flex justify-between my-3">
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="Address"
                  id="Address"
                  value={personalModel.Address}
                  label={localize.address}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />

                <div className="mx-2" />

                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="PhoneNumber"
                  id="PhoneNumber"
                  value={personalModel.PhoneNumber}
                  label={localize.phone_number}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>

              <div className="flex justify-between my-3">
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  name="EmailAddress"
                  id="Email"
                  value={personalModel.EmailAddress}
                  label={localize.email}
                  variant="outlined"
                  onChange={handleChange}
                  autoComplete="off"
                />

                <div className="mx-2" />

                <Combobox
                  id="Warehouse"
                  handleValueChange={handleWarehouseChange}
                  fieldValue={personalModel?.Warehouse}
                  fieldOptions={warehouses?.Data ?? []}
                  fieldLabel={localize.warehouse}
                />
              </div>
            </section>
          </section>
        </div>
      </Container>
    </div>
  );
}
